import * as THREE from 'three';

export default class ThreeSequenceRenderer {
    constructor(canvas) {
        this.canvas = canvas;
        this.isWebGL = true;

        try {
            this.renderer = new THREE.WebGLRenderer({
                canvas: this.canvas,
                alpha: true,
                antialias: false,
                powerPreference: 'high-performance',
            });
            this.renderer.setPixelRatio(window.innerWidth <= 768 ? 1 : window.devicePixelRatio);
            this.renderer.setSize(window.innerWidth, window.innerHeight);

            this.scene = new THREE.Scene();

            // Orthographic camera sized to match viewport
            this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

            // Fullscreen plane geometry
            this.geometry = new THREE.PlaneGeometry(2, 2);

            // Texture placeholder - will be updated with frame images
            this.texture = new THREE.Texture();
            this.texture.colorSpace = THREE.SRGBColorSpace;
            this.texture.minFilter = THREE.LinearFilter;
            this.texture.magFilter = THREE.LinearFilter;
            this.texture.generateMipmaps = false;

            this.material = new THREE.MeshBasicMaterial({
                map: this.texture,
                depthTest: false,
                depthWrite: false,
            });

            this.mesh = new THREE.Mesh(this.geometry, this.material);
            this.scene.add(this.mesh);

        } catch (e) {
            // Fallback to Canvas 2D
            console.warn('WebGL not available, falling back to Canvas 2D:', e);
            this.isWebGL = false;
            this.context = this.canvas.getContext('2d');
            this._setupCanvas2DSize();
        }
    }

    _setupCanvas2DSize() {
        const pixelRatio = window.innerWidth <= 768 ? 1 : window.devicePixelRatio;
        this.canvas.width = window.innerWidth * pixelRatio;
        this.canvas.height = window.innerHeight * pixelRatio;
        this.canvas.style.width = window.innerWidth + 'px';
        this.canvas.style.height = window.innerHeight + 'px';
        this.context.scale(pixelRatio, pixelRatio);
    }

    updateFrame(image) {
        if (!image || !image.complete || !image.naturalWidth) return;

        if (this.isWebGL) {
            this._renderWebGL(image);
        } else {
            this._renderCanvas2D(image);
        }
    }

    _renderWebGL(image) {
        // Update texture with new image
        this.texture.image = image;
        this.texture.needsUpdate = true;

        // Calculate UV scaling for "cover" behavior
        const canvasAspect = window.innerWidth / window.innerHeight;
        const imageAspect = image.naturalWidth / image.naturalHeight;

        let scaleX = 1, scaleY = 1;
        if (imageAspect > canvasAspect) {
            // Image is wider than canvas - scale X down to fit height
            scaleX = canvasAspect / imageAspect;
        } else {
            // Image is taller than canvas - scale Y down to fit width
            scaleY = imageAspect / canvasAspect;
        }

        this.mesh.scale.set(1 / scaleX, 1 / scaleY, 1);
        this.renderer.render(this.scene, this.camera);
    }

    _renderCanvas2D(image) {
        const canvasWidth = window.innerWidth;
        const canvasHeight = window.innerHeight;

        this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);

        const imageAspect = image.naturalWidth / image.naturalHeight;
        const canvasAspect = canvasWidth / canvasHeight;

        let drawWidth, drawHeight, drawX, drawY;

        if (imageAspect > canvasAspect) {
            drawHeight = canvasHeight;
            drawWidth = drawHeight * imageAspect;
            drawX = (canvasWidth - drawWidth) / 2;
            drawY = 0;
        } else {
            drawWidth = canvasWidth;
            drawHeight = drawWidth / imageAspect;
            drawX = 0;
            drawY = (canvasHeight - drawHeight) / 2;
        }

        this.context.drawImage(image, drawX, drawY, drawWidth, drawHeight);
    }

    resize() {
        if (this.isWebGL) {
            this.renderer.setSize(window.innerWidth, window.innerHeight);
        } else {
            this._setupCanvas2DSize();
        }
    }

    dispose() {
        if (this.isWebGL) {
            this.texture.dispose();
            this.material.dispose();
            this.geometry.dispose();
            this.renderer.dispose();
        }
        this.mesh = null;
        this.scene = null;
        this.camera = null;
    }
}
